import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { getBadges } from '../api/platform';
import { BadgeProp } from '../components/Badges';
import { getCurrentCellStore } from '../utils/util';
import { BADGE_FILTER_CONFIGS } from '../constants/constants';

// Badge filtering configurations mapped to cell settings
const BADGE_FILTER_MODE_CONFIGS = {
  training: {
    primaryCategories: BADGE_FILTER_CONFIGS.TRAINING_CATEGORIES,
    streakBadgeIds: BADGE_FILTER_CONFIGS.TRAINING_STREAK_BADGES
  },
  phishing: {
    primaryCategories: BADGE_FILTER_CONFIGS.PHISHING_CATEGORIES,
    streakBadgeIds: BADGE_FILTER_CONFIGS.PHISHING_STREAK_BADGES
  }
};

type BadgeFilterMode = keyof typeof BADGE_FILTER_MODE_CONFIGS | null;

export interface IBadgesData {
  badges: BadgeProp[];
  total_badges: number;
  total_earned: number;
}

export interface GetBadgesEventsObjectState {
  badgesData: IBadgesData;
  isLoading: boolean;
  error?: object;
}

const initialState: GetBadgesEventsObjectState = {
  badgesData: {
    badges: [],
    total_badges: 0,
    total_earned: 0
  },
  isLoading: false,
  error: {}
};

/**
 * Helper function to determine badge filter mode based on cell settings
 */
const getBadgeFilterMode = (cell: any): BadgeFilterMode => {
  if (cell?.show_training_data && !cell?.show_phishing_data) return 'training';
  if (cell?.show_phishing_data && !cell?.show_training_data) return 'phishing';
  return null;
};

/**
 * Helper function to filter badges based on mode
 */
const filterBadgesByMode = (badges: BadgeProp[], mode: BadgeFilterMode): BadgeProp[] => {
  if (!mode) return badges;
  
  const config = BADGE_FILTER_MODE_CONFIGS[mode];
  
  return badges.filter((badge) => {
    // Include if badge is in primary categories
    if (config.primaryCategories.includes(badge.category)) {
      return true;
    }
    
    // Include if badge is a streak/special badge in the allowed list
    if (badge.category === 'streak_and_special' && config.streakBadgeIds.includes(badge.badgeId)) {
      return true;
    }
    
    return false;
  });
};

export const getBadgesEvents = createAsyncThunk('getBadgesPlatform', async () => {
  const response = await getBadges();
  if (!response) return initialState;
  
  const cell = getCurrentCellStore();
  const mode = getBadgeFilterMode(cell);
  
  // Filter badges based on cell configuration
  const filteredBadges = filterBadgesByMode(response.badges, mode);
  
  // Recalculate totals after filtering
  const earnedBadges = filteredBadges.filter((badge) => badge.status === 'unlocked');
  
  return {
    badges: filteredBadges,
    total_badges: filteredBadges.length,
    total_earned: earnedBadges.length
  };
});


export const getBadgeSlice = createSlice({
  name: 'getBadgesEvents',
  initialState,
  reducers: { resetBadgesEvents: () => initialState },
  extraReducers: (builder) => {
    builder.addCase(getBadgesEvents.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(getBadgesEvents.fulfilled, (state: any, action: any) => {
      state.isLoading = false;
      state.badgesData = action.payload;
      state.error = undefined;
    });

    builder.addCase(getBadgesEvents.rejected, (state: any, action: any) => {
      state.isLoading = false;
      state.badgesData = {};
      state.error = action.payload as string;
    });
  }
});

export const getBadgesEventsDataSelector = (state: RootState) => state.badgesEvents;
export default getBadgeSlice.reducer;
export const { resetBadgesEvents } = getBadgeSlice.actions;
